import "./App.css";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClientInstance } from "@/lib/query-client";
import { pagesConfig } from "./pages.config";
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from "react-router-dom";
import PageNotFound from "./lib/PageNotFound";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import PendingApproval from "@/pages/PendingApproval";

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : () => null;

const LayoutWrapper = ({ children, currentPageName }) =>
  Layout ? <Layout currentPageName={currentPageName}>{children}</Layout> : <>{children}</>;

function Spinner() {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-slate-50">
      <div className="w-8 h-8 border-4 border-slate-200 border-t-brand-ink rounded-full animate-spin" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const { isAuthenticated, loading, user } = useAuth();

  // Process OAuth callback FIRST to avoid race conditions
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }

  if (loading || isAuthenticated === null) return <Spinner />;

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // Users awaiting admin approval cannot enter the app yet
  if (user?.status === "pending") {
    return <PendingApproval />;
  }

  // External parties (DD counterparts) are restricted to the data room only
  if (user?.role === "external") {
    const allowed = ["Deals", "DataRoom"];
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/Deals" replace />} />
        {Object.entries(Pages)
          .filter(([path]) => allowed.includes(path))
          .map(([path, Page]) => (
            <Route
              key={path}
              path={`/${path}`}
              element={<LayoutWrapper currentPageName={path}><Page /></LayoutWrapper>}
            />
          ))}
        <Route path="*" element={<Navigate to="/Deals" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={<LayoutWrapper currentPageName={mainPageKey}><MainPage /></LayoutWrapper>}
      />
      {Object.entries(Pages).map(([path, Page]) => (
        <Route
          key={path}
          path={`/${path}`}
          element={<LayoutWrapper currentPageName={path}><Page /></LayoutWrapper>}
        />
      ))}
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <AppRouter />
        </Router>
        <Toaster />
        <SonnerToaster />
      </QueryClientProvider>
    </AuthProvider>
  );
}

export default App;
